import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";


import { axiosInstance, useClientInfo } from "../utils/commonImports";

export default function SalesRegister() {
  const { clientId } = useClientInfo();

  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [customer, setCustomer] = useState("ALL");

  /* ================= LOAD CUSTOMERS ================= */
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/customers", {
        params: { client_id: clientId },
      })
      .then((res) => setCustomers(res.data || []))
      .catch(console.error);
  }, [clientId]);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/invoices/sales-register", {
        params: {
          client_id: clientId,
          fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
          toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
          customer_id: customer === "ALL" ? null : customer,
        },
      })
      .then((res) => setRows(res.data || []))
      .catch(console.error);
  }, [clientId, fromDate, toDate, customer]);

  /* ================= TOTALS ================= */
  const sum = (field) => rows.reduce((t, r) => t + Number(r[field] || 0), 0);

  const totals = {
    basic: sum("basic_amount"),
    escort: sum("escort_amount"),
    batta: sum("batta_amount"),
    parking: sum("parking_amount"),
    others: sum("others_amount"),
    permit: sum("permit_amount"),
    toll: sum("toll_amount"),
    fuel: sum("fuel_esc_amount"),
    add: sum("addition_amount"),
    discount: sum("discount"),

    igst: rows
      .filter((r) => r.gst_treatment !== "Registered Business-Under RCM")
      .reduce((t, r) => t + Number(r.igst_amount || 0), 0),

    cgst: rows
      .filter((r) => r.gst_treatment !== "Registered Business-Under RCM")
      .reduce((t, r) => t + Number(r.cgst_amount || 0), 0),

    sgst: rows
      .filter((r) => r.gst_treatment !== "Registered Business-Under RCM")
      .reduce((t, r) => t + Number(r.sgst_amount || 0), 0),

    rcm_igst: rows
      .filter((r) => r.gst_treatment === "Registered Business-Under RCM")
      .reduce((t, r) => t + Number(r.igst_amount || 0), 0),

    rcm_cgst: rows
      .filter((r) => r.gst_treatment === "Registered Business-Under RCM")
      .reduce((t, r) => t + Number(r.cgst_amount || 0), 0),

    rcm_sgst: rows
      .filter((r) => r.gst_treatment === "Registered Business-Under RCM")
      .reduce((t, r) => t + Number(r.sgst_amount || 0), 0),

    net: sum("net_amount"),
  };

  const num = (v) => Number(v || 0).toFixed(2);

  const groupedData =
    customer === "ALL"
      ? rows.reduce((acc, row) => {
          const key = row.customername || "Unknown";
          if (!acc[key]) acc[key] = [];
          acc[key].push(row);
          return acc;
        }, {})
      : null;

  /* ================= UI ================= */
  return (
    <Box sx={{ mb: 1 }}>
      <Typography fontWeight={700} mb={2}>
        Sales Register
      </Typography>

      {/* FILTER BAR */}
 <Box 
  sx={{
    display: "grid",
    gridTemplateColumns: "160px 160px 260px",
    gap: 2,
    alignItems: "end",   // ⭐ Proper bottom alignment
    background: "#fafafa",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    p: 2,
  }}
  >

  <LocalizationProvider dateAdapter={AdapterDayjs}>
   <DatePicker
     label="From Date"
     format="DD/MM/YYYY"
     value={fromDate ? dayjs(fromDate) : null}
     onChange={(newValue) =>
       setFromDate(newValue ? newValue.format("YYYY-MM-DD") : "")
     }
     slotProps={{
       textField: {
         size: "small",
         sx: {
           width: 160,
           "& .MuiInputBase-root": {
             height: 34,
             fontSize: "0.75rem",
           },
           "& .MuiInputLabel-root": {
             fontSize: "0.75rem",
           },
         },
       },
     }}
   />
 
   <DatePicker
     label="To Date"
     format="DD/MM/YYYY"
     value={toDate ? dayjs(toDate) : null}
     onChange={(newValue) =>
       setToDate(newValue ? newValue.format("YYYY-MM-DD") : "")
     }
     slotProps={{
       textField: {
         size: "small",
         sx: {
           width: 160,
           "& .MuiInputBase-root": {
             height: 34,
             fontSize: "0.75rem",
           },
           "& .MuiInputLabel-root": {
             fontSize: "0.75rem",
           },
         },
       },
     }}
   />
 </LocalizationProvider>
 
  <FormControl variant="outlined" size="small">
    <InputLabel>Customer</InputLabel>
    <Select
      value={customer}
      label="Customer"
      onChange={(e) => setCustomer(e.target.value)}
    >
      <MenuItem value="ALL">All Customers</MenuItem>
      {customers.map((c) => (
        <MenuItem key={c.customerid} value={c.customerid}>
          {c.customername}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Box>



      {customer !== "ALL" && rows.length > 0 && (
        <Box
          sx={{
            backgroundColor: "#f5f5f5",
            padding: "8px 12px",
            borderLeft: "5px solid #1976d2",
            marginBottom: "8px",
            borderRadius: "4px",
            
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
            }}
          >
            Customer: {rows[0].customername}
          </Typography>
        </Box>
      )}

      {/* TABLE */}
      <Paper>
        <TableContainer sx={{ maxHeight: "75vh", border: "1px solid #999" }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: "fixed", // ⭐ VERY IMPORTANT
              minWidth: 1700, // Adjust if needed
              "& th, & td": {
                border: "1px solid #ccc",
                fontSize: "0.70rem",
                padding: "4px 6px",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableHead>
              {/* ===== GROUP HEADER ROW ===== */}
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#f4f4f4",
                    fontWeight: 700,
                    fontSize: "0.70rem",
                    border: "1px solid #9e9e9e",
                    textAlign: "center",
                    padding: "6px 8px",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell rowSpan={2} sx={{ width: 90 }}>
                  Inv No
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }}>
                  Date
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 200 }}>
                  Customer
                </TableCell>

                <TableCell rowSpan={2} sx={{ width: 75 }} align="right">
                  Basic
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Escort
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Batta
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Parking
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Others
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Permit
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Toll
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Fuel
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Addition
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }} align="right">
                  Discount
                </TableCell>

                <TableCell colSpan={3} sx={{ width: 135 }}>
                  GST %
                </TableCell>
                <TableCell colSpan={3} sx={{ width: 210 }}>
                  GST Amount
                </TableCell>
                <TableCell colSpan={3} sx={{ width: 210 }}>
                  RCM GST
                </TableCell>

                <TableCell rowSpan={2} sx={{ width: 90 }} align="right">
                  Net
                </TableCell>
              </TableRow>

              {/* ===== SUB HEADER ROW ===== */}
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#f4f4f4",
                    fontWeight: 700,
                    fontSize: "0.70rem",
                    border: "1px solid #9e9e9e",
                    textAlign: "center",
                    padding: "6px 8px",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                {/* GST % */}
                <TableCell sx={{ width: 45 }} align="right">
                  IGST%
                </TableCell>
                <TableCell sx={{ width: 45 }} align="right">
                  CGST%
                </TableCell>
                <TableCell sx={{ width: 45 }} align="right">
                  SGST%
                </TableCell>

                {/* GST Amount */}
                <TableCell sx={{ width: 70 }} align="right">
                  IGST
                </TableCell>
                <TableCell sx={{ width: 70 }} align="right">
                  CGST
                </TableCell>
                <TableCell sx={{ width: 70 }} align="right">
                  SGST
                </TableCell>

                {/* RCM GST */}
                <TableCell sx={{ width: 70 }} align="right">
                  IGST
                </TableCell>
                <TableCell sx={{ width: 70 }} align="right">
                  CGST
                </TableCell>
                <TableCell sx={{ width: 70 }} align="right">
                  SGST
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {customer === "ALL"
                ? Object.entries(
                    rows.reduce((acc, row) => {
                      const key = row.customername || "Unknown";
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(row);
                      return acc;
                    }, {}),
                  ).map(([custName, custRows], idx) => {
                    const sub = (field) =>
                      custRows.reduce((t, r) => t + Number(r[field] || 0), 0);

                    return (
                      <React.Fragment key={idx}>
                        {/* ===== CUSTOMER TITLE ROW ===== */}
                        <TableRow sx={{ backgroundColor: "#dfe6e9" }}>
                          <TableCell
                            colSpan={999}
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.85rem",
                              padding: "8px",
                            }}
                          >
                            {custName}
                          </TableCell>
                        </TableRow>

                        {/* ===== INVOICE ROWS ===== */}
                        {custRows.map((r, i) => {
                          const isRCM = r.gst_treatment
                            ?.toLowerCase()
                            .includes("rcm");

                          return (
                            <TableRow
                              key={i}
                              sx={{
                                background: i % 2 === 0 ? "#fff" : "#fafafa",
                              }}
                            >
                              <TableCell>{r.invoice_number}</TableCell>
                              <TableCell>
                                {dayjs(r.invoice_date).format("DD/MM/YYYY")}
                              </TableCell>
                              <TableCell>{r.customername}</TableCell>

                              <TableCell align="right">
                                {num(r.basic_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.escort_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.batta_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.parking_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.others_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.permit_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.toll_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.fuel_esc_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.addition_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.discount)}
                              </TableCell>

                              {/* GST % */}
                              <TableCell align="right">
                                {num(r.igst_percent)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.cgst_percent)}
                              </TableCell>
                              <TableCell align="right">
                                {num(r.sgst_percent)}
                              </TableCell>

                              {/* GST Amount */}
                              <TableCell align="right">
                                {isRCM ? "0.00" : num(r.igst_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {isRCM ? "0.00" : num(r.cgst_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {isRCM ? "0.00" : num(r.sgst_amount)}
                              </TableCell>

                              {/* RCM GST */}
                              <TableCell align="right">
                                {isRCM ? num(r.igst_amount) : "0.00"}
                              </TableCell>
                              <TableCell align="right">
                                {isRCM ? num(r.cgst_amount) : "0.00"}
                              </TableCell>
                              <TableCell align="right">
                                {isRCM ? num(r.sgst_amount) : "0.00"}
                              </TableCell>

                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {num(r.net_amount)}
                              </TableCell>
                            </TableRow>
                          );
                        })}

                        {/* ===== SUBTOTAL ROW ===== */}
                        <TableRow
                          sx={{
                            backgroundColor: "#f1f2f6",
                            "& td": {
                              fontWeight: 700,
                              borderTop: "3px solid #000",
                              borderBottom: "2px solid #000",
                              borderLeft: "1px solid #9e9e9e",
                              borderRight: "1px solid #9e9e9e",
                            },
                          }}
                        >
                          <TableCell colSpan={3}>Subtotal</TableCell>

                          <TableCell align="right">
                            {num(sub("basic_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("escort_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("batta_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("parking_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("others_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("permit_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("toll_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("fuel_esc_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("addition_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("discount"))}
                          </TableCell>

                          {/* GST % empty */}
                          <TableCell colSpan={3}></TableCell>

                          {/* GST Amount totals */}
                          <TableCell align="right">
                            {num(sub("igst_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("cgst_amount"))}
                          </TableCell>
                          <TableCell align="right">
                            {num(sub("sgst_amount"))}
                          </TableCell>

                          {/* RCM empty */}
                          <TableCell colSpan={3}></TableCell>

                          <TableCell align="right">
                            {num(sub("net_amount"))}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                : rows.map((r, i) => {
                    const isRCM = r.gst_treatment
                      ?.toLowerCase()
                      .includes("rcm");

                    return (
                      <TableRow
                        key={i}
                        sx={{
                          background: i % 2 === 0 ? "#fff" : "#fafafa",
                        }}
                      >
                        <TableCell>{r.invoice_number}</TableCell>
                        <TableCell>
                          {dayjs(r.invoice_date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell>{r.customername}</TableCell>

                        <TableCell align="right">
                          {num(r.basic_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.escort_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.batta_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.parking_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.others_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.permit_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.toll_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.fuel_esc_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.addition_amount)}
                        </TableCell>
                        <TableCell align="right">{num(r.discount)}</TableCell>

                        {/* GST % */}
                        <TableCell align="right">
                          {num(r.igst_percent)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.cgst_percent)}
                        </TableCell>
                        <TableCell align="right">
                          {num(r.sgst_percent)}
                        </TableCell>

                        {/* GST Amount */}
                        <TableCell align="right">
                          {isRCM ? "0.00" : num(r.igst_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {isRCM ? "0.00" : num(r.cgst_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {isRCM ? "0.00" : num(r.sgst_amount)}
                        </TableCell>

                        {/* RCM GST */}
                        <TableCell align="right">
                          {isRCM ? num(r.igst_amount) : "0.00"}
                        </TableCell>
                        <TableCell align="right">
                          {isRCM ? num(r.cgst_amount) : "0.00"}
                        </TableCell>
                        <TableCell align="right">
                          {isRCM ? num(r.sgst_amount) : "0.00"}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {num(r.net_amount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>

            <TableFooter>
              <TableRow
                sx={{
                  backgroundColor: "#eeeeee",
                  borderTop: "4px double #333",
                  "& td": {
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    border: "1px solid #9e9e9e",
                    padding: "6px 8px",
                  },
                }}
              >
                {/* Inv No + Date + Customer */}
                <TableCell colSpan={3}>TOTAL</TableCell>

                {/* Amount Columns */}
                <TableCell align="right">{num(totals.basic)}</TableCell>
                <TableCell align="right">{num(totals.escort)}</TableCell>
                <TableCell align="right">{num(totals.batta)}</TableCell>
                <TableCell align="right">{num(totals.parking)}</TableCell>
                <TableCell align="right">{num(totals.others)}</TableCell>
                <TableCell align="right">{num(totals.permit)}</TableCell>
                <TableCell align="right">{num(totals.toll)}</TableCell>
                <TableCell align="right">{num(totals.fuel)}</TableCell>
                <TableCell align="right">{num(totals.add)}</TableCell>
                <TableCell align="right">{num(totals.discount)}</TableCell>

                {/* ===== GST % (Empty) ===== */}
                <TableCell />
                <TableCell />
                <TableCell />

                {/* ===== GST Amount ===== */}
                <TableCell align="right">{num(totals.igst)}</TableCell>
                <TableCell align="right">{num(totals.cgst)}</TableCell>
                <TableCell align="right">{num(totals.sgst)}</TableCell>

                {/* ===== RCM GST Amount ===== */}
                <TableCell align="right">{num(totals.rcm_igst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_cgst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_sgst)}</TableCell>

                {/* ===== Net ===== */}
                <TableCell align="right">{num(totals.net)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
